import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import { Message } from 'jl-design-system/components/message/Message';
import { Modal } from 'jl-design-system/components/modal/Modal';
import { UnderlinedButton } from 'jl-design-system/elements/button/Button';

// Types
import { AppDispatch } from 'types/RootState.types';
import { GiftOptionsModalProps } from './GiftOptionsModal.types';
import giftOptionsModalState from './GiftOptionsModal.state';

// Config
import one4all from '@assets/images/logos/one4all.png';
import { GIFT_OPTIONS } from '@constants/giftOptionsConstants';
import { getGiftingPaymentDetails } from '@redux/actions/payment/paymentActions';
import { disabledEscKeyHandler } from '@utils/helpers/eventKeyActions';

// Components
import GiftOption from './GiftOption';
import Container from '@components/container';
import { ApplyGiftCard, ApplyGiftVoucher } from '@components/gift-options-forms';

// Styles
import styles from './gift-options-modal.scss';

const GiftOptionsModal = ({
  giftApplicationApiCallIsActive = false,
  redirectFromGiftCardToPayment = () => {},
  handleModalClose = () => {},
  isOpen = false,
  modalContentId = '',
  onOptionChange = () => {},
  onRemove = () => {},
  onUndo = () => {},
}: GiftOptionsModalProps) => {
  const dispatch: AppDispatch = useDispatch();
  const { getGiftingPaymentDetailsFailed } = useSelector(giftOptionsModalState);

  useEffect(() => {
    if (giftApplicationApiCallIsActive) {
      document.addEventListener('keydown', disabledEscKeyHandler, true);
    } else {
      document.removeEventListener('keydown', disabledEscKeyHandler, true);
    }

    return () => {
      document.removeEventListener('keydown', disabledEscKeyHandler, true);
    };
  }, [giftApplicationApiCallIsActive]);

  useEffect(() => {
    if (isOpen) dispatch(getGiftingPaymentDetails());
  }, [isOpen]);

  const changeToCardPayment = () => {
    handleModalClose();
    redirectFromGiftCardToPayment();
  };

  const giftOptionsReduced = [
    {
      type: GIFT_OPTIONS.GIFT_CARD,
      heading: 'Use gift card',
      giftOptionClassName: 'giftOptionGiftCard',
      dataTest: 'modal-gift-card-button',
    },
    {
      type: GIFT_OPTIONS.GIFT_VOUCHER,
      heading: 'Use gift voucher',
      giftOptionClassName: 'giftOptionGiftVoucher',
      dataTest: 'modal-gift-voucher-button',
    },
  ];

  const updateModalContent = (id: string | null) => onOptionChange(id);
  const onModalBackClick = () => updateModalContent(null);

  const renderModalContent = () => {
    if (getGiftingPaymentDetailsFailed) {
      return (
        <Container marginBottom="2">
          <Message
            body="Please try again later or proceed with your order and save your gift card or voucher for another time."
            dataTestId="gift-options-service-unavailable"
            title="Sorry, the gift card service is unavailable at this time"
            type="error"
          />
        </Container>
      );
    }

    switch (modalContentId) {
      case GIFT_OPTIONS.GIFT_CARD:
        return <ApplyGiftCard onDone={handleModalClose} onRemove={onRemove} onUndo={onUndo} renderingContext="modal" />;
      case GIFT_OPTIONS.GIFT_VOUCHER:
        return (
          <ApplyGiftVoucher onDone={handleModalClose} onRemove={onRemove} onUndo={onUndo} renderingContext="modal" />
        );
      default:
        return (
          <Container flex={{ alignItems: 'center', flexDirection: 'col' }}>
            <Container className={cx(styles.giftOptionsList)} flex={{ justifyContent: 'center' }} width="full">
              {giftOptionsReduced.map((props) => (
                <GiftOption onClick={() => updateModalContent(props.type)} {...props} key={props.type} />
              ))}
            </Container>
          </Container>
        );
    }
  };

  const selectedOption = giftOptionsReduced.filter(({ type }) => type === modalContentId)[0];
  const heading = selectedOption ? selectedOption.heading : 'Select a payment type';
  const showBackButton = !!modalContentId;

  return (
    <Modal
      backButton={showBackButton}
      className={cx(styles.modal, {
        [styles.isProcessing]: giftApplicationApiCallIsActive,
      })}
      footer={false}
      isOpen={isOpen}
      onBackButtonClick={onModalBackClick}
      onClose={handleModalClose}
      title={heading}
      width="large"
    >
      <Container flex={{ alignItems: 'center', flexDirection: 'col' }}>{renderModalContent()}</Container>

      {!modalContentId && (
        <Container marginTop="3" testId="one4all-button" textAlign="center">
          <p>
            Got a One4All gift card?{' '}
            <UnderlinedButton data-testid="prepaid-card-hint-button" onClick={changeToCardPayment}>
              Click here to enter your details
            </UnderlinedButton>
          </p>
          <img alt="one4all" src={one4all} width="70" />
        </Container>
      )}
    </Modal>
  );
};

export default GiftOptionsModal;
